import type { MimeTypeExtended } from '@sosocio/creator-types/app';
import { fromBuffer } from 'file-type/browser';
import type { ImageLoadFailureDiagnostics } from 'services/load-image';
import { UnsupportedFileTypeError } from 'settings/errors';
import {
	MIME_TYPES_HEIC,
	MIME_TYPES_PDF,
	MIME_TYPES_REQUIRE_CONVERSION,
} from 'settings/filetypes';
import { getImagePreviewFailureKind } from 'utils/image-load-failure';
import inspectImageHeader, {
	createImageHeaderReadErrorDiagnostics,
	type ImageHeaderDiagnostics,
} from 'utils/inspect-image-header';
import { getExifPhotoDate } from 'utils/photo-date';
import reportError, { isErrorReported } from 'utils/report-error';

export type ImageDetails = {
	orientation?: number;
	fullWidth?: number;
	fullHeight?: number;
	photodate?: string;
	thumbUrl?: string | null;
	fcx?: number;
	fcy?: number;
	fcw?: number;
	fch?: number;
	/**
	 * Set when the selected file cannot safely provide a browser preview, either
	 * because inspection failed or because the format requires server conversion.
	 * Callers stop serving the raw copy until a processed model becomes available.
	 */
	previewUnusable?: boolean;
};

type ImageDetailsOptions = {
	maxWidth: number;
	maxHeight: number;
	faceRecognition: boolean;
	failureDiagnostics?: ImageLoadFailureDiagnostics;
};

export type MimeResolutionSource = 'browser-declared' | 'extension' | 'magic-bytes';

type ResolvedFileData = {
	file: File;
	fileType: MimeTypeExtended;
	date: number;
	headerDiagnostics?: ImageHeaderDiagnostics;
	mimeSource: MimeResolutionSource;
	sniffedMime?: string;
};

const IMAGE_HEADER_SCAN_BYTES = 65536;

/**
 * Inspect header bytes without letting diagnostic parsing affect upload flow.
 *
 * @param bytes Bytes already read for MIME detection.
 * @param fileSize Total local file size.
 * @returns Privacy-safe header diagnostics, including a safe fallback.
 */
function inspectImageHeaderSafely(
	bytes: Uint8Array,
	fileSize: number,
): ImageHeaderDiagnostics {
	try {
		return inspectImageHeader(
			bytes,
			fileSize,
		);
	} catch {
		return createImageHeaderReadErrorDiagnostics();
	}
}

/**
 * Classify conversion-required files or inspect browser-renderable files without
 * allowing preview failures to block their upload.
 *
 * @param file Local file selected for upload.
 * @param options Decode bounds, face recognition, and safe failure context.
 * @param fileType Creator's accepted MIME type for conversion routing.
 * @returns Extracted image details or a classified local-preview signal.
 */
export async function detectImageDetails(
	file: File,
	options: ImageDetailsOptions,
	fileType?: MimeTypeExtended,
): Promise<ImageDetails> {
	if (
		fileType
		&& MIME_TYPES_REQUIRE_CONVERSION.includes(fileType)
	) {
		return {
			previewUnusable: true,
		};
	}

	const loadImage = (await import('services/load-image')).default;

	try {
		const { data } = await loadImage(
			file,
			{
				faceRecognition: options.faceRecognition,
				meta: true,
				orientation: false,
				parallel: 2,
				failureDiagnostics: options.failureDiagnostics,
			},
		);

		const orientation = data?.exif
			? data.exif[0x0112] as number
			: undefined;

		let fullWidth: number | undefined;
		let fullHeight: number | undefined;

		if (data) {
			/*
			 * EXIF orientation values 5-8 are the four rotated-by-90 variants
			 * that swap width and height. Values 1-4 keep the axes and anything
			 * outside 1-8 is invalid, so only swap within the 5-8 range.
			 */
			if (orientation && orientation >= 5 && orientation <= 8) {
				fullWidth = data.originalHeight;
				fullHeight = data.originalWidth;
			} else {
				fullWidth = data.originalWidth;
				fullHeight = data.originalHeight;
			}
		}

		if (fullWidth && fullHeight) {
			const scale = Math.max(
				1,
				fullWidth / options.maxWidth,
				fullHeight / options.maxHeight,
			);
			fullWidth = Math.round(fullWidth / scale);
			fullHeight = Math.round(fullHeight / scale);
		}

		let fch: number | undefined;
		let fcw: number | undefined;
		let fcx: number | undefined;
		let fcy: number | undefined;

		if (data?.faces?.boundingBox && fullWidth && fullHeight) {
			const { boundingBox } = data.faces;
			fcx = Math.round(boundingBox.x1 * fullWidth);
			fcy = Math.round(boundingBox.y1 * fullHeight);
			fcw = Math.round((boundingBox.x2 - boundingBox.x1) * fullWidth);
			fch = Math.round((boundingBox.y2 - boundingBox.y1) * fullHeight);
		}

		const photodate = getExifPhotoDate(data?.exif);

		return {
			orientation,
			fullWidth,
			fullHeight,
			photodate,
			thumbUrl: (
				// @ts-expect-error: `Thumbnail` is missing in the type definition
				data?.exif?.Thumbnail
					// @ts-expect-error: `Thumbnail` is missing in the type definition
					? data.exif.Thumbnail
					: null
			),
			fcx,
			fcy,
			fcw,
			fch,
		};
	} catch (error) {
		const previewFailureKind = getImagePreviewFailureKind(error);

		/*
		 * Image inspection is best-effort: a failure here must not block the
		 * upload, so we return the preview signal. Blueimp decode failures report
		 * at their source with richer context; errors from any other inspection
		 * step still need to be reported here.
		 */
		if (!isErrorReported(error)) {
			reportError(
				error instanceof Error
					? error
					: new Error('Failed to inspect image details'),
				'warning',
			);
		}

		/*
		 * Only a known browser decode/validation failure makes the local preview
		 * unusable. Unrelated inspection failures remain best-effort and do not
		 * create sticky preview state.
		 */
		return (
			previewFailureKind
				? {
					previewUnusable: true,
				}
				: {}
		);
	}
}

/**
 * Resolve an accepted MIME type and retain privacy-safe provenance from the
 * same bounded header read.
 *
 * @param file Local file selected for upload.
 * @param acceptedFileTypes MIME types enabled for the current upload context.
 * @returns Resolved file metadata and bounded header diagnostics.
 */
export function resolveFileType(
	file: File,
	acceptedFileTypes: MimeTypeExtended[],
): Promise<ResolvedFileData> {
	/**
	 * Validate a candidate MIME and retain how it was selected for diagnostics.
	 *
	 * @param candidateMime MIME candidate to validate against the accepted list.
	 * @param mimeSource Boundary that supplied the accepted candidate.
	 * @param sniffedMime MIME returned by byte detection, when available.
	 * @param headerDiagnostics Bounded header profile from the same byte read.
	 * @returns Resolved file metadata used by the upload controller.
	 */
	const buildResult = (
		candidateMime: string,
		mimeSource: MimeResolutionSource,
		sniffedMime?: string,
		headerDiagnostics?: ImageHeaderDiagnostics,
	): ResolvedFileData => {
		/*
		 * `file.type` is a plain string that may be empty when the browser cannot
		 * determine the type. Narrow it against the accepted list instead of
		 * casting, so the returned `fileType` is a genuine MimeTypeExtended.
		 */
		const acceptedType = acceptedFileTypes.find((type) => type === candidateMime);

		if (!acceptedType) {
			throw new UnsupportedFileTypeError(`Filetype ${candidateMime || 'unknown'} not allowed`);
		}

		return {
			file,
			fileType: acceptedType,
			date: file.lastModified,
			headerDiagnostics,
			mimeSource,
			sniffedMime,
		};
	};

	if (window.FileReader) {
		return new Promise((resolve, reject) => {
			/*
			 * Resolve with the magic-byte type when it is accepted, otherwise fall
			 * back to the browser-reported file.type before giving up. file-type
			 * cannot fingerprint text formats — an SVG with an `<?xml` prolog sniffs
			 * as application/xml, which is not an accepted type — so without this
			 * fallback such uploads were silently dropped. The pre-XState controller
			 * fell back the same way; #1581 dropped it, which broke SVG uploads.
			 */
			/**
			 * Prefer an accepted byte-sniffed MIME, retaining it when browser MIME
			 * fallback is required for formats the sniffer cannot identify.
			 *
			 * @param detectedMime MIME returned by `file-type`, when available.
			 * @param headerDiagnostics Header profile parsed from the same bytes.
			 */
			const settle = (
				detectedMime?: string,
				headerDiagnostics?: ImageHeaderDiagnostics,
			): void => {
				try {
					resolve(buildResult(
						(
							detectedMime
							?? file.type
						),
						(
							detectedMime
								? 'magic-bytes'
								: 'browser-declared'
						),
						detectedMime,
						headerDiagnostics,
					));
				} catch (detectedError) {
					if (detectedMime === undefined) {
						reject(detectedError);

						return;
					}

					try {
						resolve(buildResult(
							file.type,
							'browser-declared',
							detectedMime,
							headerDiagnostics,
						));
					} catch {
						reject(detectedError);
					}
				}
			};

			const blob = file.slice(
				0,
				IMAGE_HEADER_SCAN_BYTES,
			);
			const reader = new FileReader();

			reader.onloadend = (e) => {
				const result = e.target?.result;
				const ready = (
					e.target
					&& e.target.readyState === FileReader.DONE
					&& result
					&& typeof result !== 'string'
				);

				if (!ready) {
					settle(
						undefined,
						createImageHeaderReadErrorDiagnostics(),
					);

					return;
				}

				const bytes = new Uint8Array(result as ArrayBuffer);
				const headerDiagnostics = inspectImageHeaderSafely(
					bytes,
					file.size,
				);

				void Promise
					.resolve()
					.then(() => fromBuffer(bytes))
					.then((detected) => {
						settle(
							detected?.mime,
							headerDiagnostics,
						);
					})
					.catch(() => {
						settle(
							undefined,
							headerDiagnostics,
						);
					});
			};

			try {
				reader.readAsArrayBuffer(blob);
			} catch {
				settle(
					undefined,
					createImageHeaderReadErrorDiagnostics(),
				);
			}
		});
	}

	const lowerFileName = file.name.toLowerCase();
	/**
	 * Preserve the asynchronous API when byte inspection is unavailable.
	 *
	 * @param candidateMime MIME selected by the fallback boundary.
	 * @param mimeSource Boundary that supplied the candidate.
	 * @returns Resolved file metadata or a rejected unsupported-type error.
	 */
	const buildResultAsync = (
		candidateMime: string,
		mimeSource: MimeResolutionSource,
	): Promise<ResolvedFileData> => Promise
		.resolve()
		.then(() => buildResult(
			candidateMime,
			mimeSource,
		));

	if (lowerFileName.endsWith('.heic')) {
		return buildResultAsync(
			MIME_TYPES_HEIC[0],
			'extension',
		);
	}

	if (lowerFileName.endsWith('.pdf')) {
		return buildResultAsync(
			MIME_TYPES_PDF[0],
			'extension',
		);
	}

	return buildResultAsync(
		file.type,
		'browser-declared',
	);
}
